import type { NextPage } from 'next'
import { HOMESTEAD_SERIES_EPISODE_1_ID } from '@/constants/episode-ids'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { Seo } from '@/layout/Seo'
import {
  FranchiseExtended,
  FranchisePageData,
  getFranchiseBySlug,
  getFranchisePageBySlug,
} from '@/services/FranchiseService'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { getWatchableEpisodeByGuid } from '@/services/ProjectsService'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { FranchiseView } from '@/views/FranchiseView'

interface FranchisePageProps {
  locale: string
  preview: boolean
  pageData?: FranchisePageData | null
  franchiseSlug: string
  franchise: FranchiseExtended
  isGuildMember?: boolean
  hasWatchableReasons?: boolean
}

const FranchisePage: NextPage<FranchisePageProps> = ({
  locale,
  pageData,
  franchiseSlug,
  franchise,
  isGuildMember = false,
  hasWatchableReasons = false,
}) => {
  const { t } = useTranslate('watch')
  const path = `${paths.franchise.index}/${franchiseSlug}`
  const canonicalUrl = `${getBasePath(locale)}${path}`
  const franchiseName = franchise?.name
  const franchiseDescription = franchise?.description
  const title = t('angelFranchiseTitle', '{{ franchiseName }} Franchise | Watch all things {{ franchiseName }}', {
    franchiseName,
  })
  const description = t(
    'angelFranchiseMetaDescription',
    'Watch all things {{ franchiseName }} | {{ franchiseDescription }}',
    {
      franchiseName,
      franchiseDescription,
    },
  )
  return (
    <>
      <Seo
        canonical={canonicalUrl}
        description={description}
        openGraph={{ title, description, url: canonicalUrl }}
        path={path}
        title={title}
      />
      <FranchiseView
        pageData={pageData}
        franchise={franchise}
        isGuildMember={isGuildMember}
        slug={franchiseSlug}
        hasWatchableReasons={hasWatchableReasons}
      />
    </>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<FranchisePageProps>(
  async ({ federationClient, locale, next404, nextRedirect, params, preview, userId }) => {
    const franchiseSlug = getItemOrFirstEntry(params?.slug)
    if (!franchiseSlug) return next404()

    const [franchise, guildStatus, pageData] = await Promise.all([
      getFranchiseBySlug(franchiseSlug, true),
      getGuildStatus(federationClient),
      getFranchisePageBySlug({ slug: franchiseSlug, opts: { locale, preview } }),
    ])

    if (!franchise) return nextRedirect(paths.watch.index)

    let hasWatchableReasons = false
    if (userId && franchiseSlug === slugs.homestead) {
      const watchableEpisode = await getWatchableEpisodeByGuid(HOMESTEAD_SERIES_EPISODE_1_ID, federationClient)
      hasWatchableReasons = Boolean(
        watchableEpisode?.watchableReasons?.length && watchableEpisode?.watchableReasons.length > 0,
      )
    }

    return {
      props: {
        locale,
        preview,
        guildStatus,
        franchiseSlug,
        pageData: pageData ?? null,
        franchise,
        hasWatchableReasons,
        isGuildMember: Boolean(guildStatus && guildStatus?.isGuildMember),
        ...(await loadTranslations(locale, ['common', 'watch', 'ads'])),
      },
    }
  },
)

export default FranchisePage
